import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Upload, X, Palette } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DEFAULT_THEME_COLORS = [
  '#0f766e', '#0891b2', '#7c3aed', '#be185d',
  '#b45309', '#1d4ed8', '#15803d', '#9f1239',
];

export const PropertyDialog = ({ open, onOpenChange, property, onSave }) => {
  const [formData, setFormData] = useState({
    organisationId: '', name: '', email: '',
    phoneCountryCode: '', phone: '', address: '',
    countryId: '', stateId: '', cityId: '',
    logoUrl: '', themeColor: '#0f766e',
  });

  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [logoPreview, setLogoPreview] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) fetchOrganisationsAndCountries();
  }, [open]);

  const fetchOrganisationsAndCountries = async () => {
    try {
      const [orgsRes, countriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/organisations`),
        axios.get(`${BACKEND_URL}/api/countries`),
      ]);
      if (orgsRes.data.success) setOrganisations(orgsRes.data.organisations);
      if (countriesRes.data.success) setCountries(countriesRes.data.countries);
    } catch { setOrganisations([]); setCountries([]); }
  };

  useEffect(() => {
    if (property) {
      setFormData({
        organisationId: property.organisationId || '',
        name: property.name || '',
        email: property.email || '',
        phoneCountryCode: property.phoneCountryCode || '',
        phone: property.phone || '',
        address: property.address || '',
        countryId: property.countryId || '',
        stateId: property.stateId || '',
        cityId: property.cityId || '',
        logoUrl: property.logoUrl || '',
        themeColor: property.themeColor || '#0f766e',
      });
      setLogoPreview(property.logoUrl || '');
      if (property.countryId) fetchStatesByCountry(property.countryId);
      if (property.stateId) fetchCitiesByState(property.stateId);
    } else {
      setFormData({
        organisationId: '', name: '', email: '',
        phoneCountryCode: '', phone: '', address: '',
        countryId: '', stateId: '', cityId: '',
        logoUrl: '', themeColor: '#0f766e',
      });
      setLogoPreview('');
      setAvailableStates([]);
      setAvailableCities([]);
    }
  }, [property, open]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2 MB'); return; }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setLogoPreview(base64);
      setFormData((prev) => ({ ...prev, logoUrl: base64 }));
      setUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setLogoPreview('');
    setFormData((prev) => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fetchStatesByCountry = async (countryId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/states/country/${countryId}`);
      if (res.data.success) setAvailableStates(res.data.states);
    } catch { setAvailableStates([]); }
  };

  const fetchCitiesByState = async (stateId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/cities/state/${stateId}`);
      if (res.data.success) setAvailableCities(res.data.cities);
    } catch { setAvailableCities([]); }
  };

  const handleCountryChange = (value) => {
    fetchStatesByCountry(value);
    setAvailableCities([]);
    setFormData({ ...formData, countryId: value, stateId: '', cityId: '' });
  };

  const handleStateChange = (value) => {
    fetchCitiesByState(value);
    setFormData({ ...formData, stateId: value, cityId: '' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{property ? 'Edit Property' : 'Create Property'}</DialogTitle>
          <DialogDescription>
            {property ? 'Update the property details below.' : 'Fill in the details to create a new property.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">

            {/* Organisation */}
            <div className="space-y-2">
              <Label>Organisation *</Label>
              <Select value={formData.organisationId} onValueChange={(v) => setFormData({ ...formData, organisationId: v })} required>
                <SelectTrigger><SelectValue placeholder="Select an organisation" /></SelectTrigger>
                <SelectContent>
                  {organisations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Property Name *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="e.g., Paradise Beach Resort" />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="property@example.com" />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <div className="flex gap-2">
                <Select value={formData.phoneCountryCode} onValueChange={(v) => setFormData({ ...formData, phoneCountryCode: v })}>
                  <SelectTrigger className="w-32"><SelectValue placeholder="Code" /></SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => <SelectItem key={c.id} value={c.code}>{c.code}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="555 000 0000" className="flex-1" />
              </div>
            </div>

            {/* Location */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Country</Label>
                  <Select value={formData.countryId} onValueChange={handleCountryChange}>
                    <SelectTrigger><SelectValue placeholder="Select country" /></SelectTrigger>
                    <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Select value={formData.stateId} onValueChange={handleStateChange} disabled={!formData.countryId}>
                    <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent>{availableStates.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Select value={formData.cityId} onValueChange={(v) => setFormData({ ...formData, cityId: v })} disabled={!formData.stateId}>
                    <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                    <SelectContent>{availableCities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea id="address" name="address" value={formData.address} onChange={handleChange} rows={3} placeholder="789 Beach Road, Miami Beach, FL 33140" />
            </div>

            {/* ── Branding Section ── */}
            <div className="pt-4 border-t">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Palette className="w-4 h-4 text-teal-600" />
                Branding
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Logo Upload */}
                <div className="space-y-2">
                  <Label>Property Logo</Label>
                  <div
                    className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="logo-upload-zone"
                  >
                    {logoPreview ? (
                      <div className="relative inline-block">
                        <img src={logoPreview} alt="Logo preview" className="h-20 w-auto max-w-full object-contain rounded-lg mx-auto" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeLogo(); }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600"
                          data-testid="remove-logo-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 py-2">
                        {uploadingLogo ? (
                          <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-slate-400" />
                            <p className="text-xs text-slate-500">Click to upload logo</p>
                            <p className="text-[10px] text-slate-400">PNG, JPG, SVG · max 2 MB</p>
                          </>
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={handleLogoFile}
                      data-testid="logo-file-input"
                    />
                  </div>
                </div>

                {/* Theme Color */}
                <div className="space-y-2">
                  <Label>Theme Color</Label>
                  <div className="space-y-3">
                    {/* Color swatches */}
                    <div className="flex flex-wrap gap-2">
                      {DEFAULT_THEME_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData({ ...formData, themeColor: color })}
                          className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110"
                          style={{
                            backgroundColor: color,
                            borderColor: formData.themeColor === color ? '#0f172a' : 'transparent',
                            boxShadow: formData.themeColor === color ? `0 0 0 2px white, 0 0 0 4px ${color}` : 'none',
                          }}
                          data-testid={`color-swatch-${color.replace('#', '')}`}
                          title={color}
                        />
                      ))}
                    </div>
                    {/* Custom color picker */}
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div
                          className="w-10 h-10 rounded-xl border-2 border-slate-200 cursor-pointer shadow-inner overflow-hidden"
                          style={{ backgroundColor: formData.themeColor }}
                          onClick={() => document.getElementById('theme-color-picker').click()}
                        />
                        <input
                          id="theme-color-picker"
                          type="color"
                          value={formData.themeColor}
                          onChange={(e) => setFormData({ ...formData, themeColor: e.target.value })}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                          data-testid="theme-color-picker"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-mono font-semibold text-slate-700">{formData.themeColor}</p>
                        <p className="text-[10px] text-slate-400">Custom colour</p>
                      </div>
                    </div>
                    {/* Preview pill */}
                    <div
                      className="rounded-xl px-3 py-2 text-white text-xs font-semibold text-center"
                      style={{ backgroundColor: formData.themeColor }}
                    >
                      Preview — {formData.name || 'Property Name'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700">
              {property ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
